import { SessionContext } from '../conversation/types';
import { CASE_LOOKUP_PRESET_ID, CASE_QUESTION_PRESET_ID, IntentMatch, Preset } from './types';

export const SCORE_EXACT_LABEL = 100;
export const SCORE_PENDING = 90;
export const SCORE_CASE_NUMBER = 80;
export const SCORE_CASE_QUESTION = 80;
export const SCORE_PATTERN = 50;

const CASE_NUMBER = /\b(\d{4,})\b/;

/** Presets whose patterns still win when a case number is typed; each reads `caseRefNo` itself. */
const CASE_AWARE_PRESET_IDS = ['case_delay', 'who_to_contact', 'report_problem', 'contact_support', 'end_chat'];

/** Asking for something to be done to the case, or saying something is wrong with it: never a status ask. */
const CASE_CHANGE_REQUEST = /\b(chang\w*|modif\w*|switch\w*|swap\w*|adjust\w*|add|adding|remov\w*|cancel\w*|redo|re do|remake|replac\w*|fix|fixing|shade|colou?r|material|tooth|teeth|margin|contact point|occlusion|bite|wrong|incorrect|mistake|issue|problem|update (the|it|to)|make (it|the))\b/;

/** Words that ask where a case is up to. */
const STATUS_ASK = /\b(status|where|how|update|updates|progress|eta|ready|done|finished|when|check|look|lookup|details?|info|information|about|news|show|see|view|open|track|tracking|latest)\b/;

/** Words that carry no intent next to a case number. */
const FILLER = /\b(case|cases|no|number|ref|my|the|on|for|of|is|it|this|that|hi|hello|hey|ok|okay|so|and|yet|please|thanks|thank you)\b|#/g;

/**
 * Next to a case number: true when the rest asks about status, or is nothing at all ("721323918",
 * "case #721323918"). A change or a complaint about the case is never a status ask.
 */
export function isStatusAsk(text: string, caseRefNo: string): boolean {
  const rest = normalize(text).replace(caseRefNo, ' ');
  if (CASE_CHANGE_REQUEST.test(rest)) return false;
  if (STATUS_ASK.test(rest)) return true;
  return rest.replace(FILLER, ' ').trim() === '';
}

export function normalize(text: string | null | undefined): string {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/[^a-z0-9#'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function extractCaseNumber(text: string | null | undefined): string | null {
  const match = CASE_NUMBER.exec(String(text ?? ''));
  return match ? match[1] : null;
}

const containsWord = (haystack: string, word: string): boolean => {
  const needle = normalize(word);
  if (!needle) return false;
  return new RegExp(`(^|\\s)${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`).test(haystack);
};

/**
 * Picks the preset for a piece of user input.
 *
 * Order: an exact menu label → a preset waiting for a follow-up → with a case number, the intent first
 * (a case-aware pattern, then status vs. any other ask about the case) → a pattern hit → the preset
 * with the most keyword hits (ties broken by `priority`). Returns null when nothing fits.
 */
export function matchIntent(
  text: string,
  presets: Preset[],
  context?: SessionContext | null
): IntentMatch | null {
  const normalized = normalize(text);
  if (!normalized) return null;

  const byId = (id: string) => presets.find((p) => p.id === id);
  const caseRefNo = extractCaseNumber(text) || undefined;

  const exact = presets.find((p) => normalize(p.label) === normalized);
  if (exact) return { presetId: exact.id, score: SCORE_EXACT_LABEL, caseRefNo };

  if (context?.pendingIntentId && byId(context.pendingIntentId)) {
    return { presetId: context.pendingIntentId, score: SCORE_PENDING, caseRefNo };
  }

  // Intent before the number: "why is 721323918 late" is a delay question and "change the shade on
  // 721323918" is for the design team; only a status ask (or the bare number) gets the case card.
  if (caseRefNo) {
    const caseAware = presets.find((p) => CASE_AWARE_PRESET_IDS.includes(p.id)
      && (p.patterns || []).some((re) => re.test(normalized) || re.test(text)));
    if (caseAware) return { presetId: caseAware.id, score: SCORE_PATTERN, caseRefNo };
    if (!isStatusAsk(text, caseRefNo) && byId(CASE_QUESTION_PRESET_ID)) {
      return { presetId: CASE_QUESTION_PRESET_ID, score: SCORE_CASE_QUESTION, caseRefNo };
    }
    if (byId(CASE_LOOKUP_PRESET_ID)) return { presetId: CASE_LOOKUP_PRESET_ID, score: SCORE_CASE_NUMBER, caseRefNo };
  }

  let best: IntentMatch | null = null;
  let bestPriority = Number.NEGATIVE_INFINITY;
  const consider = (preset: Preset, score: number) => {
    if (score <= 0) return;
    if (!best || score > best.score || (score === best.score && preset.priority > bestPriority)) {
      best = { presetId: preset.id, score, caseRefNo };
      bestPriority = preset.priority;
    }
  };

  presets.forEach((preset) => {
    if ((preset.patterns || []).some((re) => re.test(normalized) || re.test(text))) {
      consider(preset, SCORE_PATTERN);
      return;
    }
    const hits = preset.keywords.filter((k) => containsWord(normalized, k)).length;
    consider(preset, hits);
  });

  return best;
}
